/**
 * MobileMenuHub — Page Hub plein écran pour la navigation mobile.
 *
 * Design "Flat Pro" (aligné sur CaisseVerticalHub Desktop) :
 *   - Fond uni slate-950 (aucun dégradé)
 *   - Cartes : bg-slate-800/60 + border-left 3px colorée selon la famille
 *   - Icônes en petite pastille colorée à faible opacité
 *   - Aucun grain, aucun halo, aucun dégradé décoratif
 *
 * Vue 1 (FAMILIES) : cartes 2 colonnes des 6 familles
 * Vue 2 (ITEMS)    : liste verticale des sous-modules
 * Header sticky    : titre dynamique + bouton fermer + recherche
 */
import React, { useEffect, useState } from "react";
import {
  Sunrise, LayoutGrid, Printer, Calculator, FileText, Building2, CalendarDays,
  ShoppingCart, ClipboardList, ClipboardCheck, Truck, BookOpen,
  Activity, BarChart3, Receipt, Wallet, Coins, Package, Users,
  RefreshCw, MessageSquare, Settings, KeyRound, Sparkles, Globe,
  ChevronRight, ArrowLeft, Search, Briefcase, Boxes, TrendingUp,
  BookMarked, ShieldCheck, ChefHat, X, Bell,
} from "lucide-react";

// Chaque item définit une couleur "hex" pour son accent (border-left + icône).
const MENU = [
  {
    key: "operations", label: "Opérations", icon: Briefcase, accent: "#f59e0b",
    desc: "Tables, commandes, factures",
    items: [
      { value: "tables", label: "Tables", sub: "Plan de salle", icon: LayoutGrid, hex: "#0d9488", roles: ["manager", "admin"] },
      { value: "commande", label: "Prise de commandes", sub: "Encaissement", icon: Calculator, hex: "#f59e0b", roles: ["server", "manager", "admin"] },
      { value: "bons", label: "Bons", sub: "Tickets en cours", icon: Printer, hex: "#f97316", badge: "bons" },
      { value: "invoices", label: "Factures", sub: "Historique", icon: FileText, hex: "#3b82f6" },
      { value: "proforma", label: "Proforma", sub: "Devis & offres", icon: FileText, hex: "#2563eb", roles: ["manager", "admin"] },
      { value: "locations", label: "Locations", sub: "Salles & équipements", icon: Building2, hex: "#a855f7", roles: ["manager", "admin"] },
      { value: "planning", label: "Planning hebdo", sub: "Équipes & horaires", icon: CalendarDays, hex: "#3b82f6", roles: ["manager", "admin"], testid: "tab-planning" },
    ],
  },
  {
    key: "stock", label: "Stock & Appro", icon: Boxes, accent: "#0ea5e9",
    desc: "Achats, besoins, fournisseurs",
    items: [
      { value: "achats", label: "Achats", sub: "Dépenses & sorties", icon: ShoppingCart, hex: "#a855f7", badge: "achats", roles: ["admin"] },
      { value: "courses", label: "Appro Manager", sub: "Demandes terrain", icon: ShoppingCart, hex: "#f59e0b", roles: ["admin"], testid: "tab-appro-manager" },
      { value: "needs", label: "Besoins", sub: "Listing du terrain", icon: ClipboardList, hex: "#6366f1", badge: "needs", roles: ["manager", "admin"] },
      { value: "valid_demands", label: "Demandes valides", sub: "Besoins · Achats · Bons crédit", icon: ClipboardCheck, hex: "#10b981", roles: ["manager", "admin"] },
      { value: "field_stock", label: "Point de stock", sub: "Inventaire temps réel", icon: ClipboardCheck, hex: "#10b981", badge: "field_stock", roles: ["manager", "admin"] },
    ],
  },
  {
    key: "finances", label: "Finances & Rapports", icon: TrendingUp, accent: "#10b981",
    desc: "Stats, journal, comptes",
    items: [
      { value: "journee", label: "Journée", sub: "Ouverture / Clôture", icon: Sunrise, hex: "#f59e0b", roles: ["admin", "manager"] },
      { value: "point_caisse", label: "Point de la Caisse", sub: "Fin de journée", icon: Receipt, hex: "#10b981", roles: ["admin", "manager"] },
      { value: "stats", label: "Statistiques", sub: "Performance globale", icon: BarChart3, hex: "#22c55e", badge: "stats", roles: ["admin"] },
      { value: "forecasts", label: "Journal", sub: "Mouvements détaillés", icon: BookOpen, hex: "#a855f7", roles: ["admin"] },
      { value: "hebdo", label: "Faire le point", sub: "Synthèse hebdo", icon: BarChart3, hex: "#06b6d4", roles: ["manager", "admin"] },
      { value: "current-accounts", label: "Compte courant", sub: "Solde & relevés", icon: Wallet, hex: "#059669", roles: ["admin"] },
      { value: "salaries", label: "Salaires", sub: "Employés & fiches de paie", icon: Wallet, hex: "#10b981", roles: ["admin"] },
      { value: "tips", label: "Pourboires", sub: "Cagnotte & distribution", icon: Coins, hex: "#eab308", badge: "tips" },
    ],
  },
  {
    key: "catalog", label: "Catalogue & Clients", icon: BookMarked, accent: "#ec4899",
    desc: "Produits, clients, notes",
    items: [
      { value: "products", label: "Produits", sub: "Référentiel", icon: Package, hex: "#a855f7" },
      { value: "clients", label: "Clients", sub: "Fidélité & CRM", icon: Users, hex: "#ec4899" },
      { value: "subscriptions", label: "Abonnements", sub: "Forfaits récurrents", icon: RefreshCw, hex: "#06b6d4", roles: ["manager", "admin"] },
      { value: "instructions", label: "Notes", sub: "Mémo équipe", icon: MessageSquare, hex: "#14b8a6", badge: "notes", roles: ["manager", "admin"] },
    ],
  },
  {
    key: "notifications", label: "Notifications", icon: Bell, accent: "#f59e0b",
    desc: "Toutes les notifications en attente",
    items: [
      { value: "notifications", label: "Toutes les notifications", sub: "Liste consolidée cliquable", icon: Bell, hex: "#f59e0b", roles: ["manager", "admin"] },
    ],
  },
  {
    key: "admin", label: "Administration", icon: ShieldCheck, accent: "#ef4444",
    desc: "Utilisateurs, audit, avis",
    items: [
      { value: "users", label: "Utilisateurs", sub: "Comptes & droits", icon: Settings, hex: "#ef4444", roles: ["admin"] },
      { value: "guest-codes", label: "Codes Invité", sub: "Accès temporaires", icon: KeyRound, hex: "#f59e0b", roles: ["admin"] },
      { value: "audit", label: "Audit", sub: "Traçabilité complète", icon: ClipboardList, hex: "#f43f5e", roles: ["admin"] },
      { value: "customer-reviews", label: "Avis clients", sub: "Feedback public", icon: MessageSquare, hex: "#ec4899", badge: "reviews", roles: ["admin"] },
      { value: "site-notifications", label: "Notifs Site", sub: "Bannières & alertes", icon: Globe, hex: "#10b981", roles: ["admin"] },
    ],
  },
];

const SOLO = [
  { value: "mon_point", label: "Mon Point", sub: "Mes performances", icon: ClipboardList, hex: "#6366f1", roles: ["server"] },
  { value: "points_agents", label: "Points Agents", sub: "Suivi équipe", icon: ClipboardList, hex: "#6366f1", roles: ["manager"] },
];

const visible = (item, role) => {
  if (item.hideRoles?.includes(role)) return false;
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.includes(role);
};

export default function MobileMenuHub({
  open,
  onClose,
  activeTab,
  setActiveTab,
  currentUser,
  badges = {},
  unreadNotesCount = 0,
  fieldStockPendingCount = 0,
  cuisineNeedsPending = { pending: 0, urgent: 0 },
}) {
  const role = currentUser?.role || "server";
  const [view, setView] = useState("families"); // 'families' | 'items'
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [search, setSearch] = useState("");

  const families = MENU
    .map((f) => ({ ...f, items: f.items.filter((i) => visible(i, role)) }))
    .filter((f) => f.items.length > 0);
  const soloItems = SOLO.filter((i) => visible(i, role));

  useEffect(() => {
    if (!open) {
      setTimeout(() => { setView("families"); setSelectedFamily(null); setSearch(""); }, 200);
    }
  }, [open]);

  const resolveBadge = () => 0;

  const familyBadgeTotal = (family) =>
    family.items.reduce((acc, it) => acc + (it.badge ? resolveBadge(it.badge) : 0), 0);

  const goToItem = (value) => {
    setActiveTab(value);
    onClose?.();
  };

  // Recherche globale (cross-family)
  const allItems = [
    ...families.flatMap((f) => f.items.map((i) => ({ ...i, familyKey: f.key, familyLabel: f.label, familyAccent: f.accent }))),
    ...soloItems.map((i) => ({ ...i, familyKey: "solo", familyLabel: "Mon espace", familyAccent: "#6366f1" })),
  ];
  const searchResults = search.trim()
    ? allItems.filter((i) =>
        i.label.toLowerCase().includes(search.toLowerCase()) ||
        (i.sub || "").toLowerCase().includes(search.toLowerCase()) ||
        i.familyLabel.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  if (!open) return null;

  const currentFamily = selectedFamily ? families.find((f) => f.key === selectedFamily) : null;

  return (
    <div
      className="mmh-root fixed inset-0 z-[8500] bg-slate-950 md:hidden flex flex-col"
      data-testid="mobile-menu-hub"
    >
      <style>{`
        .mmh-root, .mmh-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }

        @keyframes mmh-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mmh-slide {
          from { opacity: 0; transform: translateX(14px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .mmh-fade { animation: mmh-fade-in 220ms cubic-bezier(0.16,1,0.3,1); }
        .mmh-slide { animation: mmh-slide 200ms cubic-bezier(0.16,1,0.3,1); }

        /* Carte flat pro — identique au desktop CaisseVerticalHub */
        .mmh-card {
          position: relative;
          width: 100%;
          border-radius: 12px;
          background: rgba(30, 41, 59, 0.6); /* slate-800/60 uni */
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-left-width: 3px;
          border-left-color: transparent;
          transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
          text-align: left;
        }
        .mmh-card:hover { background: rgba(51, 65, 85, 0.7); border-color: rgba(71, 85, 105, 0.6); }
        .mmh-card:active { transform: scale(0.985); }
        .mmh-card[data-active="true"] {
          background: rgba(51, 65, 85, 0.85);
          border-color: rgba(100, 116, 139, 0.5);
        }

        .mmh-icon-box {
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .mmh-group-title {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgb(100, 116, 139);
          padding: 12px 4px 6px;
        }

        .mmh-badge {
          background: rgb(239, 68, 68);
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-feature-settings: 'tnum';
        }
      `}</style>

      {/* Header sticky — uni, sans halo */}
      <div className="relative z-10 flex items-center gap-2 px-3 pt-4 pb-3 border-b border-slate-800/80 flex-shrink-0">
        {view === "items" && currentFamily ? (
          <button
            type="button"
            onClick={() => { setView("families"); setSelectedFamily(null); }}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-800/70 hover:bg-slate-800 text-white flex-shrink-0"
            data-testid="hub-back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={2.4} />
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <span className="text-slate-900 font-black text-lg">M</span>
            </div>
          </div>
        )}
        <div className="min-w-0 flex-1">
          {view === "items" && currentFamily ? (
            <>
              <p className="text-slate-500 text-[10px] uppercase tracking-[0.15em] font-bold leading-none mb-0.5">Catégorie</p>
              <p className="text-white text-[17px] font-black tracking-tight leading-tight">{currentFamily.label}</p>
            </>
          ) : (
            <>
              <p className="text-white font-black text-[17px] tracking-tight leading-tight">Caisse Pro</p>
              <p className="text-slate-500 text-[11px] uppercase tracking-[0.15em] font-bold leading-tight">Navigation</p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-800/70 hover:bg-slate-800 text-white flex-shrink-0"
          data-testid="hub-close-btn"
        >
          <X className="w-5 h-5" strokeWidth={2.4} />
        </button>
      </div>

      {/* Recherche */}
      <div className="relative z-10 px-3 pt-3 flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-900/70 border border-slate-700/60">
          <Search className="w-4 h-4 text-slate-400" strokeWidth={2.4} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un module…"
            className="flex-1 bg-transparent border-0 outline-none text-white text-[14px] placeholder:text-slate-500"
            data-testid="hub-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-slate-500 hover:text-white">
              <X className="w-3.5 h-3.5" strokeWidth={2.4} />
            </button>
          )}
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="relative z-10 flex-1 overflow-y-auto px-3 py-3 pb-6">
        {search.trim() ? (
          /* ── Résultats de recherche ── */
          <div className="space-y-2 mmh-slide">
            {searchResults.length === 0 ? (
              <p className="text-slate-500 text-sm italic text-center py-12">Aucun résultat pour « {search} »</p>
            ) : (
              searchResults.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => goToItem(item.value)}
                    data-active={isActive}
                    className="mmh-card flex items-center gap-3 p-3"
                    style={{ borderLeftColor: item.hex }}
                  >
                    <div className="mmh-icon-box w-11 h-11" style={{ background: `${item.hex}22` }}>
                      <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: item.hex }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-bold tracking-tight leading-tight text-white">{item.label}</p>
                      <p className="text-slate-500 text-[12px] leading-tight mt-0.5 truncate">
                        {item.familyLabel}{item.sub ? ` · ${item.sub}` : ""}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" strokeWidth={2.4} />
                  </button>
                );
              })
            )}
          </div>
        ) : view === "families" ? (
          /* ── Vue 1 : cartes des familles (2 colonnes, style flat) ── */
          <div className="space-y-3 mmh-fade">
            <div className="grid grid-cols-2 gap-2.5">
              {families.map((family) => {
                const Icon = family.icon;
                const bCount = familyBadgeTotal(family);
                return (
                  <button
                    key={family.key}
                    type="button"
                    onClick={() => { setSelectedFamily(family.key); setView("items"); }}
                    className="mmh-card flex flex-col items-start gap-2 p-3.5"
                    style={{ borderLeftColor: family.accent }}
                    data-testid={`hub-family-${family.key}`}
                  >
                    {bCount > 0 && (
                      <span className="mmh-badge absolute top-2.5 right-2.5 min-w-[22px] h-[22px] px-2 rounded-md text-[11px] font-bold flex items-center justify-center">
                        {bCount > 99 ? "99+" : bCount}
                      </span>
                    )}
                    <div className="mmh-icon-box w-12 h-12" style={{ background: `${family.accent}22` }}>
                      <Icon className="w-6 h-6" strokeWidth={2.2} style={{ color: family.accent }} />
                    </div>
                    <div className="min-w-0 w-full">
                      <p className="text-white text-[14.5px] font-black tracking-tight leading-tight">{family.label}</p>
                      <p className="text-slate-500 text-[11.5px] leading-tight mt-0.5 truncate">{family.desc}</p>
                    </div>
                    <div className="flex items-center justify-between w-full mt-0.5">
                      <p className="text-slate-500 text-[10.5px] font-bold uppercase tracking-wider">
                        {family.items.length} module{family.items.length > 1 ? "s" : ""}
                      </p>
                      <ChevronRight className="w-4 h-4 text-slate-500" strokeWidth={2.6} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Solo items */}
            {soloItems.length > 0 && (
              <div className="pt-2 space-y-2">
                <p className="mmh-group-title">Mon espace</p>
                {soloItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => goToItem(item.value)}
                      className="mmh-card flex items-center gap-3 p-3"
                      style={{ borderLeftColor: item.hex }}
                    >
                      <div className="mmh-icon-box w-11 h-11" style={{ background: `${item.hex}22` }}>
                        <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: item.hex }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-[15px] font-bold tracking-tight leading-tight">{item.label}</p>
                        <p className="text-slate-500 text-[12px] leading-tight">{item.sub}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" strokeWidth={2.4} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ── Vue 2 : items de la famille sélectionnée ── */
          <div className="space-y-2 mmh-slide">
            {currentFamily?.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.value;
              const bVal = item.badge ? resolveBadge(item.badge) : 0;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => goToItem(item.value)}
                  data-active={isActive}
                  className="mmh-card flex items-center gap-3 p-3.5"
                  style={{ borderLeftColor: item.hex }}
                  data-testid={`hub-item-${item.value}`}
                >
                  <div className="mmh-icon-box w-12 h-12" style={{ background: `${item.hex}22` }}>
                    <Icon className="w-5.5 h-5.5 w-6 h-6" strokeWidth={2.2} style={{ color: item.hex }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-[15.5px] font-black tracking-tight leading-tight">{item.label}</p>
                    {item.sub && (
                      <p className="text-slate-500 text-[12px] leading-tight mt-0.5">{item.sub}</p>
                    )}
                  </div>
                  {bVal > 0 && (
                    <span className="mmh-badge min-w-[22px] h-[22px] px-2 rounded-md text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                      {bVal > 99 ? "99+" : bVal}
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" strokeWidth={2.4} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
